import { createServerFn } from "@tanstack/react-start";
import { getRequest, getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";
import { isBotUserAgent } from "@/lib/profile-view";
import {
  QR_SCAN_EVENT_SOURCE,
  QR_SCAN_EVENT_TYPE,
  QR_SCAN_PARAM,
  QR_SCAN_PARAM_VALUE,
  decideQrScan,
} from "@/lib/qr-scan";

const RecordQrScanSchema = z.object({
  /** Nominee slug taken from the QR destination URL — the ONLY attribution input. */
  slug: z.string().trim().min(1).max(200),
  /** The QR marker value present in the arriving URL. */
  src: z.string().trim().max(40),
  /** Per-browser-session identifier used for once-per-session dedupe. */
  sessionId: z.string().trim().min(8).max(120),
  referrer: z.string().max(500).optional().nullable(),
});

type Skip =
  | "admin_or_unverified"
  | "bot"
  | "no_attribution"
  | "not_found"
  | "owner"
  | "duplicate_session"
  | "error";

/**
 * Records ONE `qr_code_scan` for the nominee resolved from the QR destination slug.
 *
 * Security model (identical to the proven `profile_view` path):
 * - Attribution comes from the server-side slug lookup; a client-supplied
 *   nominee id is never accepted, so credit cannot be redirected.
 * - Writes use the narrowly scoped server-side service path for this one event
 *   type only. `log_recognition_event` keeps its authenticated-only grant and
 *   `anon` gains no new execute rights; no RLS policy is changed.
 * - Owner self-arrivals are dropped.
 */
export const recordQrScan = createServerFn({ method: "POST" })
  .validator((d: unknown) => RecordQrScanSchema.parse(d))
  .handler(async ({ data }): Promise<{ ok: boolean; skipped?: Skip }> => {
    const req = (() => {
      try {
        return getRequest();
      } catch {
        return null;
      }
    })();
    const ua = req?.headers?.get("user-agent") ?? null;
    if (isBotUserAgent(ua)) return { ok: false, skipped: "bot" };

    const qrAttributed = data.src.toLowerCase() === QR_SCAN_PARAM_VALUE;
    if (!qrAttributed) return { ok: false, skipped: "no_attribution" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { canCollectRequestStats } = await import("@/lib/stats-collection.server");
    if (!await canCollectRequestStats(req, supabaseAdmin)) {
      return { ok: false, skipped: "admin_or_unverified" };
    }

    const ref = data.slug;
    let { data: nominee } = await supabaseAdmin
      .from("nominees")
      .select("id, status, claimed_by_user_id, deleted_at")
      .eq("slug", ref)
      .maybeSingle();

    if (!nominee && /^[0-9a-f-]{36}$/i.test(ref)) {
      const byId = await supabaseAdmin
        .from("nominees")
        .select("id, status, claimed_by_user_id, deleted_at")
        .eq("id", ref)
        .maybeSingle();
      nominee = byId.data as typeof nominee;
    }
    const row = nominee as
      | {
          id: string;
          status: string | null;
          claimed_by_user_id: string | null;
          deleted_at: string | null;
        }
      | null;
    const resolved =
      row && !row.deleted_at && row.status === "active"
        ? { id: row.id, claimedByUserId: row.claimed_by_user_id }
        : null;

    let viewerUserId: string | null = null;
    const auth = req?.headers?.get("authorization") ?? "";
    if (auth.startsWith("Bearer ")) {
      const token = auth.slice(7).trim();
      if (token.split(".").length === 3) {
        const { data: claims } = await supabaseAdmin.auth.getClaims(token);
        viewerUserId = (claims?.claims?.sub as string | undefined) ?? null;
      }
    }

    let sessionAlreadyScanned = false;
    if (resolved) {
      const { data: existing } = await supabaseAdmin
        .from("recognition_events")
        .select("id")
        .eq("nominee_id", resolved.id)
        .eq("event_type", QR_SCAN_EVENT_TYPE)
        .eq("session_id", data.sessionId)
        .limit(1);
      sessionAlreadyScanned = (existing ?? []).length > 0;
    }

    const decision = decideQrScan({
      userAgent: ua,
      qrAttributed,
      resolved,
      viewerUserId,
      sessionAlreadyScanned,
    });
    if (!decision.record) return { ok: false, skipped: decision.skipped };

    const nomineeId = decision.nomineeId;
    const eventId = crypto.randomUUID();
    const { error } = await supabaseAdmin.from("recognition_events").insert({
      id: eventId,
      nominee_id: nomineeId,
      event_type: QR_SCAN_EVENT_TYPE,
      event_source: QR_SCAN_EVENT_SOURCE,
      route: `/profile/${ref}?${QR_SCAN_PARAM}=${QR_SCAN_PARAM_VALUE}`,
      referrer: data.referrer ?? null,
      session_id: data.sessionId,
      user_agent: ua,
      metadata: { attribution: QR_SCAN_PARAM_VALUE, slug: ref },
    } as never);
    if (error) {
      console.error("[recordQrScan] insert failed:", error.message);
      return { ok: false, skipped: "error" };
    }

    const { recalcMilestonesSafe } = await import("@/lib/milestone-recalc.server");
    await recalcMilestonesSafe(nomineeId);

    try {
      const { observePublicRecognitionEvent } = await import("@/lib/public-event-shadow.server");
      await observePublicRecognitionEvent({
        canonicalEventId: eventId,
        eventType: QR_SCAN_EVENT_TYPE,
        nomineeId,
        requestIp: (() => {
          try {
            return getRequestIP() ?? null;
          } catch {
            return null;
          }
        })(),
        viewerUserId,
      });
    } catch (error) {
      console.warn("[recordQrScan] shadow observation failed:", error);
    }
    return { ok: true };
  });
